import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import SuperAdminLayout from '../components/SuperAdminLayout';
import { useCustomDialog } from '../context/DialogContext';
import '../styles/superadmin.css';
import ImageCropperModal from '../components/ImageCropperModal';

const BASE = 'https://menu.searchisend.in';

const SuperadminEditCustomer = () => {
    const navigate = useNavigate();
    const { alert: customAlert, confirm: customConfirm } = useCustomDialog();
    const { customerId } = useParams();
    const token = localStorage.getItem('saas_superadmin_token');
    
    const [customer, setCustomer] = useState(null);
    const [saasPlans, setSaasPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [cropImageSrc, setCropImageSrc] = useState(null);
    
    // UI state for durations
    const [durationSelection, setDurationSelection] = useState('no_change');
    const [customRenewalDate, setCustomRenewalDate] = useState('');
    const [enableFreeTrial, setEnableFreeTrial] = useState(false);
    const [trialPeriod, setTrialPeriod] = useState('7');
    const [customTrialDays, setCustomTrialDays] = useState(7);
    
    // Add/Remove credits state
    const [creditDelta, setCreditDelta] = useState('');
    const [aiCreditDelta, setAiCreditDelta] = useState('');

    // Manual Payment State
    const [manualPayment, setManualPayment] = useState({
        plan_name: 'Monthly Plan',
        amount: '',
        notes: ''
    });

    const abortControllerRef = useRef(null);
    const days = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

    useEffect(() => {
        if (!token) {
            navigate('/superadmin_v2_secure_login');
            return;
        }
        fetchCustomer();

        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
        // eslint-disable-next-line
    }, [customerId]);

    const fetchCustomer = async () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        setLoading(true);
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_dashboard.php?action=fetch_customer&customer_id=${customerId}`, {
                headers: { 'Authorization': `Bearer ${token}` },
                signal: abortControllerRef.current.signal
            });
            const data = await res.json();
            
            // Also fetch plans
            const plansRes = await fetch(`${BASE}/superadmin/v2/v2_manage_plans.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'fetch_all' })
            });
            const plansData = await plansRes.json();
            if (plansData.success) {
                setSaasPlans(plansData.plans);
            }

            if (data.success && data.customer) {
                setCustomer(data.customer);
                setCustomRenewalDate(data.customer.renewal_date ? data.customer.renewal_date.split(' ')[0] : '');
            } else {
                await customAlert(data.message || 'Customer not found', 'Not Found', 'warning');
                navigate('/superadmin_v2_secure');
            }
        } catch (err) {
            if (err.name !== 'AbortError') {
                console.error('Error fetching customer', err);
            }
        } finally {
            if (abortControllerRef.current && !abortControllerRef.current.signal.aborted) {
                setLoading(false);
            }
        }
    };

    const handleChange = (field, value) => {
        setCustomer({ ...customer, [field]: value });
    };

    const handleApplyToAllDays = () => {
        const sourceOpen = customer.open_time_sun;
        const sourceClose = customer.close_time_sun;
        const sourceClosed = customer.is_closed_sun;

        const updated = { ...customer };
        days.slice(1).forEach(day => {
            updated[`open_time_${day}`] = sourceOpen;
            updated[`close_time_${day}`] = sourceClose;
            updated[`is_closed_${day}`] = sourceClosed;
        });
        setCustomer(updated);
    };

    const handleLogoFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setCropImageSrc(URL.createObjectURL(file));
            e.target.value = ''; // Reset input
        }
    };

    const handleCropComplete = async (croppedBlob) => {
        setCropImageSrc(null);
        const formData = new FormData();
        formData.append('customer_id', customer.id);
        const file = new File([croppedBlob], 'logo.jpg', { type: 'image/jpeg' });
        formData.append('logo_image', file);

        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_dashboard.php?action=upload_customer_logo`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            const data = await res.json();
            if (data.success) {
                setCustomer({ ...customer, logo_url: data.logo_url });
                await customAlert(data.message, 'Success', 'success');
            } else {
                await customAlert(data.message, 'Error', 'danger');
            }
        } catch (err) {
            await customAlert('Upload failed', 'Error', 'danger');
        }
    };

    const handleDeleteLogo = async () => {
        if (!await customConfirm('Are you sure you want to permanently delete this logo?', 'Delete Logo', 'danger')) return;
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_dashboard.php?action=delete_customer_logo`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ customer_id: customer.id })
            });
            const data = await res.json();
            if (data.success) {
                setCustomer({ ...customer, logo_url: null });
                await customAlert(data.message, 'Success', 'success');
            } else {
                await customAlert(data.message, 'Error', 'danger');
            }
        } catch (err) {
            await customAlert('Delete failed', 'Error', 'danger');
        }
    };

    const handleRecordPayment = async (e) => {
        e.preventDefault();
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_dashboard.php?action=record_manual_payment`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    customer_id: customer.id,
                    ...manualPayment
                })
            });
            const data = await res.json();
            if (data.success) {
                await customAlert(data.message, 'Success', 'success');
                setManualPayment({ plan_name: 'Monthly Plan', amount: '', notes: '' });
                fetchCustomer(); // Refresh
            } else {
                await customAlert(data.message, 'Error', 'danger');
            }
        } catch (err) {
            await customAlert('Payment recording failed', 'Error', 'danger');
        }
    };

    const handleDeleteMenu = async () => {
        if (!await customConfirm('ARE YOU ABSOLUTELY SURE? This will permanently delete all menu items for this customer. This action cannot be undone.', 'Delete Entire Menu', 'danger')) return;
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_dashboard.php?action=delete_whole_menu`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ customer_id: customer.id })
            });
            const data = await res.json();
            await customAlert(data.message, 'Result', 'info');
        } catch (err) {
            await customAlert('Error deleting menu', 'Error', 'danger');
        }
    };

    const handleDeleteCustomer = async () => {
        if (!await customConfirm('ARE YOU ABSOLUTELY SURE? This will permanently delete this customer and all their data (menu, coupons, subscribers, etc.). This action CANNOT be undone.', 'Delete Partner Account', 'danger')) return;
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_dashboard.php?action=delete_customer`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ customer_id: customer.id })
            });
            const data = await res.json();
            if (data.success) {
                await customAlert(data.message, 'Success', 'success');
                navigate('/superadmin_v2_secure');
            } else {
                await customAlert(data.message, 'Error', 'danger');
            }
        } catch (err) {
            await customAlert('Error deleting customer', 'Error', 'danger');
        }
    };

    const computeFinalDatesAndCredits = () => {
        let finalRenewal = customer.renewal_date;
        let finalStatus = customer.status;
        let finalCredits = parseInt(customer.notification_credits || 0, 10);
        let finalAiCredits = parseInt(customer.ai_credits || 0, 10);
        let finalPopups = customer.has_offer_popup;
        let finalCoupons = customer.has_coupon_system;
        let finalPush = customer.has_push_notification;
        let finalStaff = customer.has_staff_management;
        let finalAd = customer.has_ad_monetization;
        let finalLoyalty = customer.has_loyalty_system;

        if (creditDelta) {
            finalCredits += parseInt(creditDelta, 10);
        }
        if (aiCreditDelta) {
            finalAiCredits += parseInt(aiCreditDelta, 10);
        }

        if (enableFreeTrial) {
            const d = trialPeriod === 'custom' ? parseInt(customTrialDays, 10) : parseInt(trialPeriod, 10);
            const nd = new Date();
            nd.setDate(nd.getDate() + d);
            finalRenewal = nd.toISOString().split('T')[0];
            finalStatus = 'Active';
            finalPopups = 1;
            finalCoupons = 1;
            finalPush = 1;
            finalStaff = 1;
            finalAd = 1;
            finalLoyalty = 1;
        } else if (durationSelection !== 'no_change') {
            const currentObj = new Date(customer.renewal_date);
            const today = new Date();
            let baseDate = currentObj < today ? today : currentObj;

            if (durationSelection === 'monthly') {
                baseDate.setMonth(baseDate.getMonth() + 1);
                finalRenewal = baseDate.toISOString().split('T')[0];
            } else if (durationSelection === 'annually') {
                baseDate.setFullYear(baseDate.getFullYear() + 1);
                finalRenewal = baseDate.toISOString().split('T')[0];
            } else if (durationSelection === 'lifetime') {
                baseDate.setFullYear(baseDate.getFullYear() + 100);
                finalRenewal = baseDate.toISOString().split('T')[0];
            } else if (durationSelection === 'custom') {
                finalRenewal = customRenewalDate;
            }
        }

        return { finalRenewal, finalStatus, finalCredits, finalAiCredits, finalPopups, finalCoupons, finalPush, finalStaff, finalAd, finalLoyalty };
    };

    const handleUpdateCustomer = async (e) => {
        e.preventDefault();
        setSaving(true);
        
        const computed = computeFinalDatesAndCredits();

        const payload = {
            ...customer,
            password: newPassword,
            renewal_date: computed.finalRenewal,
            status: computed.finalStatus,
            notification_credits: computed.finalCredits,
            ai_credits: computed.finalAiCredits,
            has_offer_popup: computed.finalPopups,
            has_coupon_system: computed.finalCoupons,
            has_push_notification: computed.finalPush,
            has_staff_management: computed.finalStaff,
            has_ad_monetization: computed.finalAd,
            has_loyalty_system: computed.finalLoyalty,
            customer_id: customer.id
        };

        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_dashboard.php?action=update_customer`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (data.success) {
                await customAlert('Customer updated successfully!', 'Success', 'success');
                setNewPassword('');
                setDurationSelection('no_change');
                setEnableFreeTrial(false);
                setCreditDelta('');
                setAiCreditDelta('');
                fetchCustomer();
            } else {
                await customAlert(data.message, 'Error', 'danger');
            }
        } catch (err) {
            await customAlert('Error updating customer', 'Error', 'danger');
        } finally {
            setSaving(false);
        }
    };

    if (loading || !customer) {
        return (
            <div className="superadmin-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
                    <i className="fas fa-circle-notch fa-spin fa-3x" style={{ color: 'var(--accent-primary)', marginBottom: '1rem' }}></i>
                    <p>Loading customer details...</p>
                </div>
            </div>
        );
    }

    return (
        <SuperAdminLayout>
            <div style={{ maxWidth: '900px', margin: '0 auto' }}>
                        <div style={{ marginBottom: '1.5rem' }}>
                            <Link to="/superadmin_v2_secure" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: 'var(--accent-primary)', textDecoration: 'none', fontWeight: '600', padding: '8px 16px', background: 'var(--bg-dark)', borderRadius: '50px', border: '1px solid var(--border-color)' }}>
                                <i className="fas fa-arrow-left"></i> Back to Dashboard
                            </Link>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '2rem' }}>
                            <div>
                                <h2 className="sa-page-title">Edit: {customer.restaurant_name}</h2>
                                <p className="sa-page-subtitle" style={{ margin: 0 }}>ID: {customer.id} | Subdomain: @{customer.username}</p>
                            </div>
                            <span className={`status-badge ${customer.status === 'Active' ? 'status-active' : customer.status === 'Paused' ? 'status-paused' : 'status-expired'}`} style={{ fontSize: '1rem', padding: '6px 16px' }}>
                                {customer.status}
                            </span>
                        </div>

                        {/* LOGO SECTION */}
                        <div className="sa-panel">
                            <h3 className="sa-panel-title"><i className="fas fa-image"></i> Restaurant Logo</h3>
                            {customer.logo_url ? (
                                <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '20px' }}>
                                    <img src={customer.logo_url} alt="Logo" style={{ maxWidth: '120px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-color)', background: 'white' }} />
                                    <div>
                                        <p style={{ margin: '0 0 10px 0', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Current Logo Active</p>
                                        <button onClick={handleDeleteLogo} className="sa-secondary-btn" style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }}>
                                            <i className="fas fa-trash-alt"></i> Delete Logo
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ padding: '2rem', textAlign: 'center', background: 'var(--bg-dark)', borderRadius: 'var(--radius-md)', border: '1px dashed var(--border-color)', marginBottom: '1.5rem' }}>
                                    <i className="fas fa-cloud-upload-alt fa-3x" style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}></i>
                                    <p style={{ margin: 0, color: 'var(--text-secondary)' }}>No logo has been uploaded yet.</p>
                                </div>
                            )}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <input type="file" id="logo_image" accept="image/*" className="sa-input" onChange={handleLogoFileChange} style={{ flex: 1 }} />
                            </div>
                        </div>

                        <form onSubmit={handleUpdateCustomer}>
                            {/* LOGIN & LINK DETAILS */}
                            <div className="sa-panel">
                                <h3 className="sa-panel-title"><i className="fas fa-user-circle"></i> Basic Details</h3>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                                    <div className="sa-form-group">
                                        <label className="sa-form-label">Restaurant Name</label>
                                        <input type="text" className="sa-input" style={{ width: '100%' }} value={customer.restaurant_name || ''} onChange={(e) => handleChange('restaurant_name', e.target.value)} required />
                                    </div>
                                    <div className="sa-form-group">
                                        <label className="sa-form-label">Owner Name</label>
                                        <input type="text" className="sa-input" style={{ width: '100%' }} value={customer.owner_name || ''} onChange={(e) => handleChange('owner_name', e.target.value)} />
                                    </div>
                                    <div className="sa-form-group">
                                        <label className="sa-form-label">Menu URL Slug <span style={{fontWeight: 'normal', color: 'var(--text-secondary)'}}>(e.g., "grill-inn")</span></label>
                                        <input type="text" className="sa-input" style={{ width: '100%' }} value={customer.menu_slug || ''} onChange={(e) => handleChange('menu_slug', e.target.value)} required />
                                    </div>
                                    <div className="sa-form-group">
                                        <label className="sa-form-label">Receipt/Customer Email</label>
                                        <input type="email" className="sa-input" style={{ width: '100%' }} value={customer.email || ''} onChange={(e) => handleChange('email', e.target.value)} required />
                                    </div>
                                    <div className="sa-form-group">
                                        <label className="sa-form-label">Admin Username</label>
                                        <input type="text" className="sa-input" style={{ width: '100%' }} value={customer.username || ''} onChange={(e) => handleChange('username', e.target.value)} required />
                                    </div>
                                    <div className="sa-form-group">
                                        <label className="sa-form-label">Owner Phone</label>
                                        <input type="text" className="sa-input" style={{ width: '100%' }} value={customer.owner_phone || ''} onChange={(e) => handleChange('owner_phone', e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="10-digit number" />
                                    </div>
                                    <div className="sa-form-group">
                                        <label className="sa-form-label">Restaurant Phone</label>
                                        <input type="text" className="sa-input" style={{ width: '100%' }} value={customer.restaurant_phone || ''} onChange={(e) => handleChange('restaurant_phone', e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="10-digit number" />
                                    </div>
                                    <div className="sa-form-group">
                                        <label className="sa-form-label">City</label>
                                        <input type="text" className="sa-input" style={{ width: '100%' }} value={customer.city || ''} onChange={(e) => handleChange('city', e.target.value)} />
                                    </div>
                                    <div className="sa-form-group">
                                        <label className="sa-form-label">State</label>
                                        <input type="text" className="sa-input" style={{ width: '100%' }} value={customer.state || ''} onChange={(e) => handleChange('state', e.target.value)} />
                                    </div>
                                    <div className="sa-form-group" style={{ gridColumn: '1 / -1' }}>
                                        <label className="sa-form-label">Restaurant Address</label>
                                        <textarea className="sa-input" style={{ width: '100%', resize: 'vertical', minHeight: '60px' }} value={customer.restaurant_address || ''} onChange={(e) => handleChange('restaurant_address', e.target.value)} />
                                    </div>
                                    <div className="sa-form-group" style={{ gridColumn: '1 / -1' }}>
                                        <label className="sa-form-label">New Password <span style={{fontWeight: 'normal', color: 'var(--warning)'}}>(Leave blank to keep current)</span></label>
                                        <input type="password" className="sa-input" style={{ width: '100%' }} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="••••••••" />
                                    </div>
                                </div>
                            </div>

                            {/* SUBSCRIPTION */}
                            <div className="sa-panel">
                                <h3 className="sa-panel-title"><i className="fas fa-id-card"></i> Subscription</h3>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                                    <div className="sa-form-group">
                                        <label className="sa-form-label">Subscription Status</label>
                                        <select className="sa-select" style={{ width: '100%' }} value={customer.status} onChange={(e) => handleChange('status', e.target.value)}>
                                            <option value="Active">Active</option>
                                            <option value="Paused">Paused</option>
                                            <option value="Expired">Expired</option>
                                        </select>
                                    </div>
                                    <div className="sa-form-group">
                                        <label className="sa-form-label">Active SaaS Plan</label>
                                        <select className="sa-select" style={{ width: '100%' }} value={customer.plan_id || 0} onChange={(e) => handleChange('plan_id', e.target.value)}>
                                            <option value={0}>Legacy / Custom (No Plan)</option>
                                            {saasPlans.map(p => (
                                                <option key={p.id} value={p.id}>{p.plan_name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="sa-form-group">
                                        <label className="sa-form-label">Extend Duration</label>
                                        <select className="sa-select" style={{ width: '100%' }} value={durationSelection} onChange={(e) => setDurationSelection(e.target.value)}>
                                            <option value="no_change">-- No Change -- ({new Date(customer.renewal_date).toLocaleDateString()})</option>
                                            <option value="monthly">Monthly (+1 month)</option>
                                            <option value="annually">Annually (+1 year)</option>
                                            <option value="lifetime">Lifetime</option>
                                            <option value="custom">Custom Date</option>
                                        </select>
                                    </div>
                                </div>
                                
                                {durationSelection === 'custom' && (
                                    <div className="sa-form-group">
                                        <label className="sa-form-label">Custom Renewal Date</label>
                                        <input type="date" className="sa-input" style={{ width: '100%' }} value={customRenewalDate} onChange={(e) => setCustomRenewalDate(e.target.value)} />
                                    </div>
                                )}

                                <div style={{ marginTop: '1rem', padding: '1rem', background: 'var(--bg-dark)', borderRadius: 'var(--radius-sm)' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 600, color: 'var(--text-primary)', cursor: 'pointer' }}>
                                        <input type="checkbox" style={{ transform: 'scale(1.2)' }} checked={enableFreeTrial} onChange={(e) => setEnableFreeTrial(e.target.checked)} />
                                        Start a New Free Trial? (Overrides other settings)
                                    </label>
                                    
                                    {enableFreeTrial && (
                                        <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid var(--border-color)', display: 'flex', gap: '15px', alignItems: 'center' }}>
                                            <div style={{ flex: 1 }}>
                                                <label className="sa-form-label">Trial Period:</label>
                                                <select className="sa-select" style={{ width: '100%' }} value={trialPeriod} onChange={(e) => setTrialPeriod(e.target.value)}>
                                                    <option value="2">2 Days</option>
                                                    <option value="4">4 Days</option>
                                                    <option value="7">7 Days</option>
                                                    <option value="custom">Custom Days</option>
                                                </select>
                                            </div>
                                            {trialPeriod === 'custom' && (
                                                <div style={{ flex: 1 }}>
                                                    <label className="sa-form-label">Custom Days:</label>
                                                    <input type="number" min="1" className="sa-input" style={{ width: '100%' }} value={customTrialDays} onChange={(e) => setCustomTrialDays(e.target.value)} />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="sa-panel">
                                <h3 className="sa-panel-title"><i className="fas fa-crown"></i> Premium Features</h3>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 600, cursor: 'pointer', background: 'var(--bg-dark)', padding: '1rem', borderRadius: 'var(--radius-sm)' }}>
                                        <input type="checkbox" style={{ transform: 'scale(1.2)' }} checked={Number(customer.has_offer_popup) === 1} onChange={(e) => handleChange('has_offer_popup', e.target.checked ? 1 : 0)} />
                                        Offer Popup
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 600, cursor: 'pointer', background: 'var(--bg-dark)', padding: '1rem', borderRadius: 'var(--radius-sm)' }}>
                                        <input type="checkbox" style={{ transform: 'scale(1.2)' }} checked={Number(customer.has_coupon_system) === 1} onChange={(e) => handleChange('has_coupon_system', e.target.checked ? 1 : 0)} />
                                        Coupon Engine
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 600, cursor: 'pointer', background: 'var(--bg-dark)', padding: '1rem', borderRadius: 'var(--radius-sm)' }}>
                                        <input type="checkbox" style={{ transform: 'scale(1.2)' }} checked={Number(customer.has_push_notification) === 1} onChange={(e) => handleChange('has_push_notification', e.target.checked ? 1 : 0)} />
                                        Push Notifications
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 600, cursor: 'pointer', background: 'var(--bg-dark)', padding: '1rem', borderRadius: 'var(--radius-sm)' }}>
                                        <input type="checkbox" style={{ transform: 'scale(1.2)' }} checked={Number(customer.has_staff_management) === 1} onChange={(e) => handleChange('has_staff_management', e.target.checked ? 1 : 0)} />
                                        Staff Management
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 600, cursor: 'pointer', background: 'var(--bg-dark)', padding: '1rem', borderRadius: 'var(--radius-sm)' }}>
                                        <input type="checkbox" style={{ transform: 'scale(1.2)' }} checked={Number(customer.has_ad_monetization) === 1} onChange={(e) => handleChange('has_ad_monetization', e.target.checked ? 1 : 0)} />
                                        Ad Monetization
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 600, cursor: 'pointer', background: 'var(--bg-dark)', padding: '1rem', borderRadius: 'var(--radius-sm)' }}>
                                        <input type="checkbox" style={{ transform: 'scale(1.2)' }} checked={Number(customer.has_loyalty_system) === 1} onChange={(e) => handleChange('has_loyalty_system', e.target.checked ? 1 : 0)} />
                                        Loyalty Program
                                    </label>
                                </div>
                            </div>

                            {/* TIMINGS */}
                            <div className="sa-panel">
                                <h3 className="sa-panel-title"><i className="far fa-clock"></i> Restaurant Timings</h3>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                                    {days.map(day => (
                                        <div key={day} style={{ background: 'var(--bg-dark)', border: '1px solid var(--border-color)', padding: '15px', borderRadius: 'var(--radius-sm)' }}>
                                            <div style={{ fontWeight: 700, marginBottom: '15px', textTransform: 'capitalize', borderBottom: '1px solid var(--border-color)', paddingBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                {day}
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.8rem', fontWeight: 500, color: 'var(--danger)', cursor: 'pointer' }}>
                                                    <input type="checkbox" checked={Number(customer[`is_closed_${day}`]) === 1} onChange={(e) => handleChange(`is_closed_${day}`, e.target.checked ? 1 : 0)} />
                                                    Closed
                                                </label>
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                <div>
                                                    <label className="sa-form-label" style={{ fontSize: '0.75rem', marginBottom: '2px' }}>Open</label>
                                                    <input type="time" className="sa-input" style={{ width: '100%', padding: '8px' }} value={customer[`open_time_${day}`] || ''} onChange={(e) => handleChange(`open_time_${day}`, e.target.value)} disabled={Number(customer[`is_closed_${day}`]) === 1} />
                                                </div>
                                                <div>
                                                    <label className="sa-form-label" style={{ fontSize: '0.75rem', marginBottom: '2px' }}>Close</label>
                                                    <input type="time" className="sa-input" style={{ width: '100%', padding: '8px' }} value={customer[`close_time_${day}`] || ''} onChange={(e) => handleChange(`close_time_${day}`, e.target.value)} disabled={Number(customer[`is_closed_${day}`]) === 1} />
                                                </div>
                                                {day === 'sun' && (
                                                    <button type="button" onClick={handleApplyToAllDays} className="sa-secondary-btn" style={{ width: '100%', marginTop: '5px', padding: '6px', fontSize: '0.8rem' }}><i className="fas fa-copy"></i> Apply to All</button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* LINKS & PAYMENTS */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                <div className="sa-panel">
                                    <h3 className="sa-panel-title"><i className="fas fa-link"></i> Social Links</h3>
                                    <div className="sa-form-group"><label className="sa-form-label">WhatsApp</label><input type="text" className="sa-input" style={{ width: '100%' }} value={customer.social_whatsapp || ''} onChange={(e) => handleChange('social_whatsapp', e.target.value)} /></div>
                                    <div className="sa-form-group"><label className="sa-form-label">Instagram</label><input type="text" className="sa-input" style={{ width: '100%' }} value={customer.social_instagram || ''} onChange={(e) => handleChange('social_instagram', e.target.value)} /></div>
                                    <div className="sa-form-group"><label className="sa-form-label">Facebook</label><input type="text" className="sa-input" style={{ width: '100%' }} value={customer.social_facebook || ''} onChange={(e) => handleChange('social_facebook', e.target.value)} /></div>
                                    <div className="sa-form-group"><label className="sa-form-label">YouTube</label><input type="text" className="sa-input" style={{ width: '100%' }} value={customer.social_youtube || ''} onChange={(e) => handleChange('social_youtube', e.target.value)} /></div>
                                    <div className="sa-form-group"><label className="sa-form-label">Google Review Link</label><input type="text" className="sa-input" style={{ width: '100%' }} value={customer.review_link || ''} onChange={(e) => handleChange('review_link', e.target.value)} /></div>
                                </div>

                                <div className="sa-panel">
                                    <h3 className="sa-panel-title"><i className="fas fa-wallet"></i> Payments & Pricing</h3>
                                    <div className="sa-form-group"><label className="sa-form-label">UPI ID</label><input type="text" className="sa-input" style={{ width: '100%' }} value={customer.upi_id || ''} onChange={(e) => handleChange('upi_id', e.target.value)} /></div>
                                    <div className="sa-form-group"><label className="sa-form-label">UPI Payee Name</label><input type="text" className="sa-input" style={{ width: '100%' }} value={customer.upi_payee_name || ''} onChange={(e) => handleChange('upi_payee_name', e.target.value)} /></div>
                                    <hr style={{ border: 'none', borderTop: '1px solid var(--border-color)', margin: '1.5rem 0' }} />
                                    <div className="sa-form-group"><label className="sa-form-label">Lifetime Discount Percentage (%)</label><input type="number" min="0" max="100" className="sa-input" style={{ width: '100%' }} value={customer.custom_discount_percent || 0} onChange={(e) => handleChange('custom_discount_percent', e.target.value)} /></div>
                                </div>
                            </div>

                            {/* CREDITS */}
                            <div className="sa-panel" style={{ background: 'var(--bg-dark)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid var(--border-color)' }}>
                                    <div>
                                        <h3 className="sa-panel-title" style={{ margin: 0, border: 'none' }}><i className="fas fa-coins"></i> Manage Notification Credits</h3>
                                        <p style={{ margin: '5px 0 0 0', color: 'var(--text-secondary)' }}>Current Balance: <strong style={{ color: 'var(--text-primary)', fontSize: '1.2rem' }}>{customer.notification_credits || 0}</strong></p>
                                    </div>
                                    <div style={{ width: '300px' }}>
                                        <label className="sa-form-label">Add/Remove (e.g. 10 or -10)</label>
                                        <input type="number" className="sa-input" style={{ width: '100%', background: 'white' }} value={creditDelta} onChange={(e) => setCreditDelta(e.target.value)} placeholder="0" />
                                    </div>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div>
                                        <h3 className="sa-panel-title" style={{ margin: 0, border: 'none' }}><i className="fas fa-brain" style={{ color: '#a855f7' }}></i> Manage AI Menu Scans</h3>
                                        <p style={{ margin: '5px 0 0 0', color: 'var(--text-secondary)' }}>Current Balance: <strong style={{ color: 'var(--text-primary)', fontSize: '1.2rem' }}>{customer.ai_credits || 0}</strong></p>
                                    </div>
                                    <div style={{ width: '300px' }}>
                                        <label className="sa-form-label">Add/Remove (e.g. 5 or -2)</label>
                                        <input type="number" className="sa-input" style={{ width: '100%', background: 'white' }} value={aiCreditDelta} onChange={(e) => setAiCreditDelta(e.target.value)} placeholder="0" />
                                    </div>
                                </div>
                            </div>

                            <button type="submit" disabled={saving} className="sa-primary-btn" style={{ width: '100%', padding: '16px', fontSize: '1.2rem', marginBottom: '3rem', boxShadow: '0 10px 25px -5px rgba(249, 115, 22, 0.4)' }}>
                                {saving ? <><i className="fas fa-spinner fa-spin"></i> Applying Enterprise Configuration...</> : <><i className="fas fa-check-circle"></i> Save All Customer Configurations</>}
                            </button>
                        </form>

                        {/* MANUAL PAYMENT */}
                        <div className="sa-panel" style={{ background: 'var(--info-bg)', borderColor: 'var(--info)' }}>
                            <h3 className="sa-panel-title" style={{ color: 'var(--info)', borderBottomColor: 'rgba(37, 99, 235, 0.2)' }}><i className="fas fa-file-invoice-dollar"></i> Record Offline Payment</h3>
                            <form onSubmit={handleRecordPayment}>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                                    <div className="sa-form-group">
                                        <label className="sa-form-label" style={{ color: 'var(--info)' }}>Plan Purchased</label>
                                        <select className="sa-select" style={{ width: '100%', background: 'white' }} value={manualPayment.plan_name} onChange={(e) => setManualPayment({...manualPayment, plan_name: e.target.value})}>
                                            <option value="Basic Monthly Plan">Basic Monthly Plan</option>
                                            <option value="Monthly Plan">Monthly Plan</option>
                                            <option value="Yearly Plan">Yearly Plan</option>
                                            <option value="Premium Plan">Premium Plan</option>
                                            <option value="Lifetime Plan">Lifetime Plan</option>
                                        </select>
                                    </div>
                                    <div className="sa-form-group">
                                        <label className="sa-form-label" style={{ color: 'var(--info)' }}>Amount Received (₹)</label>
                                        <input type="number" step="0.01" className="sa-input" style={{ width: '100%', background: 'white' }} value={manualPayment.amount} onChange={(e) => setManualPayment({...manualPayment, amount: e.target.value})} required />
                                    </div>
                                </div>
                                <div className="sa-form-group">
                                    <label className="sa-form-label" style={{ color: 'var(--info)' }}>Payment Notes / Ref ID</label>
                                    <input type="text" className="sa-input" style={{ width: '100%', background: 'white' }} value={manualPayment.notes} onChange={(e) => setManualPayment({...manualPayment, notes: e.target.value})} placeholder="e.g. Paid via Cash/NEFT" />
                                </div>
                                <button type="submit" className="sa-primary-btn" style={{ background: 'var(--info)' }}><i className="fas fa-receipt"></i> Submit Offline Payment & Extend Access</button>
                            </form>
                        </div>

                        {/* DANGER ZONE */}
                        <div className="sa-panel" style={{ background: 'var(--danger-bg)', borderColor: 'var(--danger)' }}>
                            <h3 className="sa-panel-title" style={{ color: 'var(--danger)', borderBottomColor: 'rgba(239, 68, 68, 0.2)' }}><i className="fas fa-exclamation-triangle"></i> Danger Zone</h3>
                            <p style={{ color: 'var(--danger)', marginBottom: '1.5rem', fontWeight: 500 }}>Warning: Actions performed here are permanent and cannot be undone.</p>
                            <div style={{ display: 'flex', gap: '15px' }}>
                                <button onClick={handleDeleteMenu} className="sa-secondary-btn" style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }}>
                                    <i className="fas fa-trash-alt"></i> Delete Entire Menu
                                </button>
                                <button onClick={handleDeleteCustomer} className="sa-primary-btn" style={{ background: 'var(--danger)' }}>
                                    <i className="fas fa-user-times"></i> Delete Partner Account
                                </button>
                            </div>
                        </div>

                    </div>
            {cropImageSrc && (
                <ImageCropperModal
                    imageSrc={cropImageSrc}
                    onClose={() => setCropImageSrc(null)}
                    onCropComplete={handleCropComplete}
                />
            )}
        </SuperAdminLayout>
    );
};

export default SuperadminEditCustomer;
